// Buffer-local rename planner — aliases and CTEs.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § NAV1 (amended 2026-08-23).
//
// Produces the byte edits F2 applies to the ACTIVE buffer for a name that has
// no catalog object behind it. Two engines, both reused rather than rewritten:
//
//   CTE   → `applyStructuralEdit(buffer, stmt, { op: 'rename-cte', … })`, the
//           SR-Edit planner the Visual Query Editor's "rename CTE" card already
//           ships. It coordinates the declaration, every downstream reference
//           in sibling CTE bodies + the main query, and — gated on the engine's
//           self-visibility matrix — the recursive self-reference inside the
//           body, all filtered through its scope-aware `filterToThisCte`.
//           Buffer-local rename gets that semantics for free.
//
//   ALIAS → the declaration token plus every dotted qualifier head (`u.email`)
//           that the scope-aware resolver binds to THIS declaration. Nearest-
//           binding-wins means an inner-scope alias of the same name keeps its
//           own occurrences and an outer one keeps its own.
//
// Both are confined to the cursor's enclosing statement, so a second statement
// that reuses the name is untouched.
//
// KNOWN LIMIT — a CTE whose name is also a table name reachable by SCHEMA
// qualification. `WITH orders AS (…) SELECT orders.x FROM dbo.orders` reads
// `orders.x` off the FROM-clause TABLE, not the CTE — but SED-7's
// `filterToThisCte` matches the dotted head by name and rewrites it with the
// CTE. Inherited semantics: the same call renames the same token from the
// Visual Query Editor's "rename CTE" card, so the two entry points stay
// consistent and the fix belongs in `applyStructuralEdit.cte.ts`, not here.
// Renaming an ordinary (unshadowed) CTE is unaffected.

import type { DatabaseEngineId } from '@/core/engines';

import { rangeFromOffsets } from '../../sql-grammar/document/lineIndex';
import type { SqlRange } from '../../sql-grammar/model';
import { applyStructuralEdit } from '../../sql-resolver/edit/applyStructuralEdit';
import { relationSlotKind } from '../../sql-resolver/scope/relationSlot';

import { forEachIdentifierChain, renderRenamedToken } from './bufferIdentifierScan';
import { findBufferRenameCollision } from './bufferRenameCollision';
import {
  buildBufferContext,
  findBufferRenameTarget,
  foldFor,
  parseOptionsForEngine,
  resolveAliasBinding,
  type BufferContext,
  type BufferRenameKind,
  type BufferRenameTarget,
} from './bufferRenameTarget';

/** One replacement inside a single buffer. `range` carries both byte offsets
 *  and 0-indexed line/column so callers can hand Monaco a 1-indexed IRange
 *  without re-deriving the line index. */
export type BufferEdit = {
  readonly range: SqlRange;
  readonly newText: string;
};

export type BufferRenamePlan =
  | { readonly status: 'no-target' }
  | { readonly status: 'unchanged' }
  | { readonly status: 'invalid-name'; readonly message: string }
  | { readonly status: 'collision'; readonly message: string }
  | {
      readonly status: 'ok';
      readonly kind: BufferRenameKind;
      readonly oldName: string;
      readonly newName: string;
      readonly edits: readonly BufferEdit[];
    };

export type PlanBufferRenameInput = {
  readonly source: string;
  readonly engineId: DatabaseEngineId;
  readonly cursorOffset: number;
  readonly newName: string;
};

/** Identifier shapes F2 accepts for a buffer-local name: a bare identifier or
 *  an engine-quoted one. Mirrors VQE's `renameCte` guard so the two entry
 *  points reject the same inputs. */
const IDENT_PATTERN = /^[A-Za-z_][A-Za-z0-9_$]*$|^"[^"]*"$|^`[^`]*`$|^\[[^\]]*\]$/;

export function planBufferLocalRename(input: PlanBufferRenameInput): BufferRenamePlan {
  const options = parseOptionsForEngine(input.engineId);
  const ctx = buildBufferContext(input.source, options);
  if (!ctx) return { status: 'no-target' };
  const target = findBufferRenameTarget(ctx, input.cursorOffset);
  if (!target) return { status: 'no-target' };

  const trimmed = input.newName.trim();
  if (trimmed.length === 0) return { status: 'unchanged' };
  if (!IDENT_PATTERN.test(trimmed)) {
    return {
      status: 'invalid-name',
      message: `"${trimmed}" is not a valid SQL identifier — use letters, digits and underscores, or a quoted name.`,
    };
  }
  if (foldFor(ctx, trimmed, false) === foldFor(ctx, target.name, false)) {
    return { status: 'unchanged' };
  }

  const collision = findBufferRenameCollision(ctx, target, trimmed);
  if (collision) {
    return {
      status: 'collision',
      message: `Cannot rename to "${trimmed}" — ${collision} with that name is already visible in this statement.`,
    };
  }

  const edits =
    target.kind === 'cte'
      ? planCteRename(ctx, target, trimmed)
      : planAliasRename(ctx, target, trimmed);
  if (edits.length === 0) return { status: 'no-target' };
  return {
    status: 'ok',
    kind: target.kind,
    oldName: target.name,
    newName: trimmed,
    edits,
  };
}

// ─── CTE ────────────────────────────────────────────────────────────────────

function planCteRename(
  ctx: BufferContext,
  target: BufferRenameTarget,
  newName: string,
): readonly BufferEdit[] {
  if (target.cteIndex === undefined) return [];
  try {
    const modified = applyStructuralEdit(ctx.buffer, target.statement, {
      op: 'rename-cte',
      index: target.cteIndex,
      newName,
    });
    return modified.plannedEdits.map((e) => ({ range: e.range, newText: e.replacement }));
  } catch {
    return [];
  }
}

// ─── Alias ──────────────────────────────────────────────────────────────────

function planAliasRename(
  ctx: BufferContext,
  target: BufferRenameTarget,
  newName: string,
): readonly BufferEdit[] {
  const binding = target.alias;
  if (!binding) return [];
  const lineIdx = ctx.lineIdx;
  // The declaration's OWN folded form (`aliasFolded` was produced with the
  // declaration's quotedness). Re-folding `binding.alias` as if it were
  // unquoted lowercases/uppercases a quoted declaration — on PG/Oracle that
  // makes `"U"` mismatch its own `"U".id` usages, renaming the declaration
  // alone and silently corrupting the statement.
  const folded = binding.aliasFolded;
  const out: BufferEdit[] = [
    {
      range: rangeFromOffsets(lineIdx, binding.range.startOffset, binding.range.endOffset),
      newText: renderRenamedToken(newName, quotingOf(ctx.source, binding.range.startOffset)),
    },
  ];

  forEachIdentifierChain(
    ctx.source,
    ctx.options,
    target.statement.range.startOffset,
    target.statement.range.endOffset,
    (chain) => {
      if (chain.head.start === binding.range.startOffset) return;
      if (chain.partCount < 2) return;
      if (foldFor(ctx, chain.head.value, chain.head.quoted) !== folded) return;
      if (relationSlotKind(ctx.source, chain.head.start, chain.head.end) !== 'dotted-head') return;
      // Scope-aware confirmation: nearest-binding-wins means an inner-scope
      // alias of the same name resolves to ITS declaration, not this one.
      const resolved = resolveAliasBinding(ctx, chain);
      if (!resolved || resolved.range.startOffset !== binding.range.startOffset) return;
      out.push({
        range: rangeFromOffsets(lineIdx, chain.head.start, chain.head.end),
        newText: renderRenamedToken(newName, chain.head.quoting),
      });
    },
  );
  return out;
}

function quotingOf(source: string, start: number) {
  const c = source[start];
  if (c === '[') return 'bracket' as const;
  if (c === '"') return 'double-quote' as const;
  if (c === '`') return 'backtick' as const;
  return 'unquoted' as const;
}
